import React, { useEffect, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import {
  Button, Col, Icons, Row,
} from '../../../components/base/BootstrapComponents/BootstrapComponents';
import { errorNotification, successNotification } from '../../Notifications/notificationDecorator';
import { getColorByType, getResponseError, setFormError } from '../../../utils/utils';
import { createGeography, editGeographyById } from '../actions';
import FieldDTOWithCol from '../../../components/fields/Abstract/FieldDTOWithCol';
import GeographyDTO from '../../../dto/GeographyDTO';
import CustomSelectInput from '../../../components/fields/SelectInput/CustomSelectInput';
import TextInputWithLabel from '../../../components/fields/TextInput/TextInputWithLabel';
import { GEOGRAPHY_TYPE_LIST, ZONE_TYPE_LIST } from '../../../configs';
import DatePicker from '../../../components/fields/DatePicker/DatePicker';
import { jsonTransform, numberTransform } from '../../../utils/transform';

const GEO_AREA_REQUIRED_MESSAGE = 'Необходимо выбрать область на карте';

const hasPolygonCoordinates = geographyJson => {
  const coordinates = geographyJson?.features?.[0]?.geometry?.coordinates;
  if (!Array.isArray(coordinates) || !coordinates.length) {
    return false;
  }
  const firstRing = coordinates[0];
  return Array.isArray(firstRing) && firstRing.filter(point => Array.isArray(point) && point.length >= 2).length >= 3;
};

export default function GeographyForm(props) {
  const {
    handleClose = () => {},
    loadData = () => {},
    geography = null,
    polygon = null,
    mapInstance = null,
  } = props;
  const dispatch = useDispatch();
  const [isDrawing, setIsDrawing] = useState(false);
  const [mapValidationError, setMapValidationError] = useState(false);
  const {
    reset, control, getValues, setError, setValue, watch,
  } = useForm({
    mode: 'onChange',
    defaultValues: geography || { status: 1 },
  });

  useEffect(() => {
    if (polygon) {
      const map = polygon.getMap();
      const stateMonitor = new window.ymaps.Monitor(polygon.editor.state);
      stateMonitor.add('drawing', newValue => setIsDrawing(newValue));
      if (map) {
        polygon.editor.startDrawing();
      }
      polygon.events.add('mapchange', () => {
        const map = polygon.getMap();
        if (map) {
          polygon.editor.startDrawing();
        }
      });
      polygon.geometry.events.add('change', () => {
        const coordinates = polygon.geometry.getCoordinates();
        const geography_json = createGeoObject(getValues('name'), coordinates);
        setValue('geography_json', geography_json);
        setMapValidationError(false);
      });
      return () => {
        polygon.events.remove('mapchange');
        polygon.geometry.events.remove('change');
        polygon.editor.stopDrawing();
      };
    }
  }, [polygon, mapInstance]);

  useEffect(() => {
    const subscription = watch(values => {
      if (polygon?.options && values && typeof values.geography_type !== 'undefined') {
        polygon.options.set('fillColor', getColorByType(values.geography_type));
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => {
    reset({});
    setTimeout(() => reset(geography ? new GeographyDTO(geography).getData() : {}), 100);
  }, [JSON.stringify(geography)]);

  const handleDrawing = () => {
    if (!polygon) {
      setMapValidationError(true);
      setError('geography_json', { type: 'manual', message: GEO_AREA_REQUIRED_MESSAGE }, { shouldFocus: true });
      errorNotification('Сохранение данных', GEO_AREA_REQUIRED_MESSAGE);
      return;
    }
    const map = polygon.getMap();
    if (map) {
      if (!polygon.editor.state.get('drawing')) {
        polygon.editor.startDrawing();
        return;
      }
      polygon.editor.stopDrawing();
    }
  };

  const handleSave = () => {
    const values = new GeographyDTO(getValues()).getData();
    if (!hasPolygonCoordinates(values.geography_json)) {
      setMapValidationError(true);
      setError('geography_json', { type: 'manual', message: GEO_AREA_REQUIRED_MESSAGE }, { shouldFocus: true });
      errorNotification('Сохранение данных', GEO_AREA_REQUIRED_MESSAGE);
      return;
    }
    const action = geography ? editGeographyById({ ...values, id: geography.geography_id }) : createGeography(values);
    dispatch(action)
      .then(() => {
        successNotification('Сохранение данных', 'Данные сохранены');
        loadData();
        handleClose();
      })
      .catch(response => {
        setFormError(response, setError);
        errorNotification('Сохранение данных', getResponseError(response));
      });
  };

  const createGeoObject = (name, coordinates) => ({
    type: 'FeatureCollection',
    name,
    crs: {
      type: 'name',
      properties: {
        name: 'urn:ogc:def:crs:OGC:1.3:CRS84',
      },
    },
    features: [
      {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'Polygon',
          coordinates,
        },
      },
    ],
  });

  const dto = new GeographyDTO();
  const attributes = dto.getAttributesByName();

  return (
    <div className={`list-columns card py-3 geography-form${mapValidationError ? ' geography-form-map-error' : ''}`}>
      <div className="list-header px-3 mb-3">
        <div className="fs-5 fw-semibold ">{geography ? 'Редактирование гео. объекта' : 'Новые гео. объект'}</div>
        <Button className="small-button ms-auto" variant="second" onClick={handleClose}>
          <Icons customIcon icon="close" />
        </Button>
      </div>
      <div className="list-content-wrapper">
        <div className="list-content-body px-3">
          <div />
          <FieldDTOWithCol
            control={control}
            type="text"
            component={TextInputWithLabel}
            dtoObject={attributes.name}
            variant="material"
            className="mb-3 mt-2"
          />
          <FieldDTOWithCol
            control={control}
            type="text"
            component={TextInputWithLabel}
            dtoObject={attributes.description}
            variant="material"
            textarea
            className="mb-3"
          />
          <FieldDTOWithCol
            control={control}
            type="text"
            component={CustomSelectInput}
            dtoObject={attributes.geography_type}
            variant="material"
            withLabel
            options={[GEOGRAPHY_TYPE_LIST[0], GEOGRAPHY_TYPE_LIST[1]]}
            // options={GEOGRAPHY_TYPE_LIST}
            className="mb-3"
          />
          <FieldDTOWithCol
            control={control}
            type="text"
            component={DatePicker}
            dtoObject={attributes.effective_date}
            variant="material"
            withLabel
            className="mb-3"
            changeFormat="x"
            // transform={numberTransform}
          />
          <FieldDTOWithCol
            control={control}
            type="text"
            component={DatePicker}
            dtoObject={attributes.published_date}
            variant="material"
            withLabel
            className="mb-3"
            changeFormat="x"
            // transform={numberTransform}
          />
          <FieldDTOWithCol
            control={control}
            type="text"
            component={DatePicker}
            dtoObject={attributes.retire_date}
            variant="material"
            withLabel
            className="mb-3"
            changeFormat="x"
            // transform={numberTransform}
          />
          <Row>
            <Col columns={12} className="text-end">
              {!geography && !isDrawing ? (
                <Button className="small-button" variant="second" onClick={handleDrawing}>
                  <Icons icon="geo-alt" />
                </Button>
              ) : null}
              {mapValidationError ? (
                <div className="geography-map-validation">{GEO_AREA_REQUIRED_MESSAGE}</div>
              ) : null}
            </Col>
          </Row>

        </div>
      </div>
      <div className="list-footer px-3 d-flex justify-content-around">
        <Button className="flex-sm-fill me-2" onClick={() => handleSave()}>{geography ? 'Сохранить' : 'Создать'}</Button>
        <Button className="flex-sm-fill ms-2" variant="secondary" onClick={handleClose}>Отменить</Button>
      </div>
    </div>
  );
}
